import { useMemo, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useParams } from "react-router-dom";
import useProjectStore from "../../store/projectStore";
import { predesign, interpolateCurve, MACHINE_TYPES, NQ_TO_NS } from "../../utils/predesign";
import { statCurvesApi } from "../../api/statCurves";

const VARIANT_LABELS = { standard: "Standard", progressive: "Progressive", conservative: "Conservative" };

function ReadRow({ label, symbol, unit, value }) {
  return (
    <div className="flex items-center justify-between py-2.5 border-b border-gray-100 last:border-0">
      <div>
        <span className="text-sm text-gray-700">{label}</span>
        {symbol && <span className="ml-1.5 text-xs text-gray-400 font-mono">{symbol}</span>}
      </div>
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium text-gray-900 w-32 text-right tabular-nums">
          {value ?? "—"}
        </span>
        {unit && <span className="text-xs text-gray-400 w-14">{unit}</span>}
      </div>
    </div>
  );
}

function ResultTile({ label, value, unit, highlight }) {
  return (
    <div className={`rounded-xl px-4 py-3 text-center ${highlight ? "bg-bh-50 border border-bh-100" : "bg-gray-50"}`}>
      <p className="text-[10px] font-mono text-gray-400 mb-0.5">{label}</p>
      <p className={`text-base font-bold tabular-nums ${highlight ? "text-bh-800" : "text-gray-800"}`}>
        {value}
        {unit && <span className="text-xs font-normal text-gray-400 ml-0.5">{unit}</span>}
      </p>
    </div>
  );
}

export default function HydraulicLayoutPage() {
  const { projectId } = useParams();
  const { activeProject } = useProjectStore();
  const [statCurves, setStatCurves] = useState([]);

  useEffect(() => {
    statCurvesApi.list().then(({ data }) => setStatCurves(data)).catch(() => {});
  }, []);

  const lc = useMemo(() => activeProject?.layout_config ?? {}, [activeProject?.layout_config]);
  const hasConfig = !!(lc.n && lc.Q && lc.H);
  const variant = lc.variant ?? "standard";

  const results = useMemo(() => {
    const { n, Q, H, n_stages = 1 } = lc;
    if (!n || !Q || !H || H <= 0 || Q <= 0 || n <= 0) return null;
    const H_stage = H / Math.max(1, n_stages);
    const nq = n * Math.sqrt(Q) / Math.pow(H_stage, 0.75);
    const machineType = nq >= 70 ? "mixed_flow" : "radial";
    const interp = (name) => interpolateCurve(statCurves, name, variant, nq);
    return predesign({
      n, Q, H, n_stages, machineType,
      psi2_stat:  interp("psi2_vs_nq"),
      phi1_stat:  interp("phi1_vs_nq"),
      b2_d2_stat: interp("b2_d2_vs_nq"),
      beta2_stat: interp("beta2_vs_nq"),
      z_d2_stat:  interp("z_d2_vs_nq"),
    });
  }, [lc, statCurves, variant]);

  if (!hasConfig) {
    return (
      <div className="p-6 max-w-xl">
        <div className="bg-gray-50 rounded-xl border border-dashed border-gray-200 p-8 text-center">
          <p className="text-sm text-gray-500 mb-2">No layout target set for this project.</p>
          <Link to={`/projects/${projectId}/layout`}
            className="text-sm text-bh-600 hover:text-bh-700 font-medium hover:underline">
            Open Layout Tool →
          </Link>
        </div>
      </div>
    );
  }

  const machineTypeMeta = results
    ? MACHINE_TYPES.find(m => m.value === (results.nq >= 70 ? "mixed_flow" : "radial"))
    : null;
  const usingStatCurves = results?.source === "stat";
  const variantLabel = VARIANT_LABELS[variant] ?? "Standard";

  return (
    <div className="p-6 space-y-5 max-w-xl">

      {/* Project design point */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              Project Layout Target
            </p>
            <p className="text-xs text-gray-400 mt-0.5">Set in the project Layout Tool.</p>
          </div>
          <Link to={`/projects/${projectId}/layout`}
            className="text-xs text-bh-600 hover:text-bh-700 hover:underline">
            Edit →
          </Link>
        </div>

        <ReadRow label="Rotational speed" symbol="n"        unit="rpm"  value={lc.n} />
        <ReadRow label="Flow rate"        symbol="Q"        unit="m³/s" value={lc.Q} />
        <ReadRow label="Head"             symbol="H"        unit="m"    value={lc.H} />
        <ReadRow label="Stages"           symbol="n_stages" unit="—"    value={lc.n_stages ?? 1} />
        <ReadRow label="Design variant"   unit="—"          value={variantLabel} />
        {machineTypeMeta && (
          <ReadRow label="Correlation" unit="—" value={machineTypeMeta.label} />
        )}
      </div>

      {/* nq / ns */}
      {results && (
        <div className="bg-bh-50 border border-bh-100 rounded-lg px-4 py-1">
          <div className="flex items-center justify-between py-2.5 border-b border-bh-100">
            <div>
              <span className="text-sm text-gray-700">Specific speed</span>
              <span className="ml-1.5 text-xs text-gray-400 font-mono">n_q</span>
              <span className="ml-2 text-[10px] text-gray-400 italic">
                SI · per stage{results.n_stages > 1 ? ` (total ${results.nq_total.toFixed(1)})` : ""}
              </span>
            </div>
            <span className="text-lg font-bold text-bh-800 tabular-nums font-mono">
              {results.nq.toFixed(1)}
            </span>
          </div>
          <div className="flex items-center justify-between py-2.5">
            <div>
              <span className="text-sm text-gray-700">Specific speed</span>
              <span className="ml-1.5 text-xs text-gray-400 font-mono">n_s</span>
              <span className="ml-2 text-[10px] text-gray-400 italic">US · rpm, GPM, ft</span>
            </div>
            <span className="text-lg font-bold text-bh-800 tabular-nums font-mono">
              {(results.nq * NQ_TO_NS).toFixed(0)}
            </span>
          </div>
        </div>
      )}

      {/* Dimensionless coefficients */}
      {results && (
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-4">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              Dimensionless Coefficients
            </p>
            <span className={`text-[10px] rounded px-2 py-0.5 border ${
              usingStatCurves
                ? "text-bh-700 bg-bh-50 border-bh-200"
                : "text-amber-600 bg-amber-50 border-amber-200"
            }`}>
              {usingStatCurves ? `Stat curves · ${variantLabel}` : "Estimates · Gülich"}
            </span>
          </div>
          <div className="grid grid-cols-4 gap-2">
            <ResultTile label="ψ₂"    value={results.psi.toFixed(3)}   unit="—" highlight />
            <ResultTile label="φ₁"    value={results.phi.toFixed(3)}   unit="—" highlight />
            <ResultTile label="b₂/D₂" value={results.b2_d2.toFixed(3)} unit="—" />
            <ResultTile label="β₂"    value={results.beta2.toFixed(1)} unit="°" />
          </div>
        </div>
      )}

      {/* Preliminary sizing */}
      {results && (
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-4">
            Preliminary Sizing
          </p>

          {results.warn && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2 mb-3">
              ⚠ {results.warn}
            </p>
          )}

          <div className={`grid gap-2 mb-2 ${results.z_mm != null ? "grid-cols-4" : "grid-cols-3"}`}>
            <ResultTile label="D₂" value={results.D2_mm.toFixed(0)} unit="mm" highlight />
            <ResultTile label="b₂" value={results.b2_mm.toFixed(1)} unit="mm" highlight />
            <ResultTile label="D₁" value={results.D1_mm.toFixed(0)} unit="mm" highlight />
            {results.z_mm != null && (
              <ResultTile label="z" value={results.z_mm.toFixed(1)} unit="mm" />
            )}
          </div>
          <div className="grid grid-cols-1 gap-2">
            <ResultTile label="β₂" value={results.beta2.toFixed(1)} unit="°" />
          </div>
        </div>
      )}

    </div>
  );
}
